import { useMemo, useState } from 'react';
import './DataTable.css';

/*
 * Sortable, filterable table — the replacement for ui-grid.
 *
 * ui-grid was used in nine places across /history plus /league, but only
 * ever for enableColumnMenus, enableAutoFitColumns, cellTemplate and (once)
 * grouping. This covers the first three; grouping is handled by the caller
 * passing pre-grouped rows.
 *
 * Columns:
 *   key        field on the row object
 *   label      header text
 *   title      header tooltip
 *   numeric    right-align, tabular figures, numeric sort
 *   render     (value, row) => node, replacing cellTemplate
 *   tone       (value, row) => string, a data-tone for cell background
 *   strong     the column identifying the row (a name), given more weight
 *   searchable set false to keep a text column out of the filter
 *   width      optional fixed column width
 *   sortable   default true
 *   defaultSort 'asc' | 'desc' — the first *visible* one wins, so a column
 *              that may be hidden can name a fallback further down the list
 */
export default function DataTable({
	columns,
	rows,
	filterPlaceholder = 'Filter…',
	filterable = true,
	emptyMessage = 'Nothing to show.',
	caption,
	maxHeight,
	/* Cap the width of narrow tables. A two-column table stretched across a
	   wide container leaves its label and its figure at opposite edges. */
	maxWidth,
	rowKey,
	/* Drop columns no row has a value for. Absent, not zero: a season where
	   nobody was sent off still shows RC, but one predating expected goals
	   does not show xG. */
	hideEmptyColumns = false,
}) {
	const [query, setQuery] = useState('');

	const visible = useMemo(() => {
		if (!hideEmptyColumns || !rows.length) return columns;
		return columns.filter((c) => rows.some((row) => row[c.key] != null));
	}, [columns, rows, hideEmptyColumns]);

	const initial = useMemo(() => {
		const col = visible.find((c) => c.defaultSort);
		return col ? { key: col.key, dir: col.defaultSort } : null;
	}, [visible]);
	const [sort, setSort] = useState(initial);
	/* A sort survives a season change, so it can outlive its own column. */
	const activeSort = sort && visible.some((c) => c.key === sort.key) ? sort : initial;

	/*
	 * Only text columns are searched. Numbers make a text filter useless —
	 * "5" would match a rank, a points total and a card count at once — and
	 * a column can opt out with searchable: false where matching it would
	 * be surprising.
	 */
	const searchable = useMemo(
		() => visible.filter((c) => !c.numeric && c.searchable !== false),
		[visible],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return rows;
		return rows.filter((row) =>
			searchable.some((c) => String(row[c.key] ?? '').toLowerCase().includes(q)),
		);
	}, [rows, searchable, query]);

	const sorted = useMemo(() => {
		if (!activeSort) return filtered;
		const col = visible.find((c) => c.key === activeSort.key);
		if (!col) return filtered;
		const factor = activeSort.dir === 'desc' ? -1 : 1;
		return [...filtered].sort((a, b) => {
			const av = a[activeSort.key];
			const bv = b[activeSort.key];
			if (av == null) return 1;
			if (bv == null) return -1;
			if (col.numeric) return (Number(av) - Number(bv)) * factor;
			return String(av).localeCompare(String(bv)) * factor;
		});
	}, [filtered, activeSort, visible]);

	const toggleSort = (col) => {
		if (col.sortable === false) return;
		setSort((prev) =>
			prev?.key === col.key
				? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
				: { key: col.key, dir: col.numeric ? 'desc' : 'asc' },
		);
	};

	return (
		<div className="data-table" style={maxWidth ? { maxWidth } : undefined}>
			{filterable && (
				<div className="data-table__toolbar">
					<input
						className="data-table__filter"
						type="search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={filterPlaceholder}
						aria-label={filterPlaceholder}
					/>
					<span className="data-table__count numeric">
						{sorted.length}
						{sorted.length !== rows.length && ` of ${rows.length}`} rows
					</span>
				</div>
			)}

			<div className="scroll-x data-table__scroll" style={maxHeight ? { maxHeight } : undefined}>
				<table className="data-table__table">
					{caption && <caption className="data-table__caption">{caption}</caption>}
					<thead>
						<tr>
							{visible.map((col) => {
								const active = activeSort?.key === col.key;
								return (
									<th
										key={col.key}
										scope="col"
										style={col.width ? { width: col.width } : undefined}
										className={col.numeric ? 'is-numeric' : undefined}
										aria-sort={
											active
												? activeSort.dir === 'asc'
													? 'ascending'
													: 'descending'
												: 'none'
										}
									>
										{col.sortable === false ? (
											<span title={col.title}>{col.label}</span>
										) : (
											<button
												type="button"
												className="data-table__sort"
												onClick={() => toggleSort(col)}
												title={col.title}
											>
												{col.label}
												<span className="data-table__arrow" aria-hidden="true">
													{active ? (activeSort.dir === 'asc' ? '▲' : '▼') : '⇅'}
												</span>
											</button>
										)}
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{sorted.length === 0 ? (
							<tr>
								<td colSpan={visible.length} className="data-table__empty">
									{emptyMessage}
								</td>
							</tr>
						) : (
							sorted.map((row, i) => (
								<tr key={rowKey ? rowKey(row) : i}>
									{visible.map((col) => (
										<td
											key={col.key}
											className={
												[col.numeric && 'is-numeric numeric', col.strong && 'is-strong']
													.filter(Boolean)
													.join(' ') || undefined
											}
											data-tone={col.tone ? col.tone(row[col.key], row) : undefined}
										>
											{col.render ? col.render(row[col.key], row) : row[col.key]}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}
